/**
 * Authentication API
 *
 * Endpoints for user authentication, registration, password management,
 * MFA, OAuth, and session management.
 *
 * NOTE: For backward compatibility, this module also includes user management
 * methods (getUsers, updateUser, deleteUser, etc.) which are also available
 * in the dedicated usersApi module.
 */

import { apiClient } from './client'
import type {
  User,
  LoginRequest,
  RegisterRequest,
  ApiResponse,
  UserSession,
  ProfileUpdateRequest,
  PublicProfile,
  PasswordValidationResult,
  BackupCodesResponse
} from './types'

/**
 * Authentication and authorization endpoints
 */
export const authApi = {
  // ==================== Core Authentication ====================

  /**
   * Login with email and password
   */
  login: (credentials: LoginRequest): Promise<ApiResponse<{ user: User; token: string }>> =>
    apiClient.post('/users/auth/login', {
      email: credentials.email,
      password: credentials.password,
      ...(credentials.mfaCode ? { mfa_code: credentials.mfaCode } : {}),
    }),

  /**
   * #528 — Email-first login. Identify which sign-in method an email uses so the
   * login page can branch (password field / Google-only / set-password-first).
   * Enumeration-safe: unknown/non-active emails resolve to 'password'.
   */
  loginIdentify: (email: string): Promise<ApiResponse<{ method: 'password' | 'google' | 'setup' }>> =>
    apiClient.post('/users/auth/login/identify', { email }),

  /**
   * #528 — Request a set-password (account-setup) email for an account that has
   * no password yet. Always resolves with a generic confirmation message
   * (enumeration-safe). Emails a tokenized link landing on /auth/complete-account.
   */
  requestAccountSetup: (email: string): Promise<ApiResponse<{ message: string }>> =>
    apiClient.post('/users/auth/request-account-setup', { email }),

  /**
   * Register a new user account
   */
  register: (data: RegisterRequest): Promise<ApiResponse<{ user: User; access_token: string; refresh_token: string; expires_in: number; verification_pending: boolean }>> =>
    apiClient.post('/users/auth/register', data),

  /**
   * #857 — Admin creates a user. Unlike register(), this endpoint does NOT set
   * auth cookies / return tokens, so an admin creating a user is not logged in
   * as that new user (which overwrote the admin session and 403'd the follow-up
   * user-list fetch). Admin-gated on the backend.
   */
  adminCreateUser: (data: RegisterRequest): Promise<ApiResponse<{ user: User; verification_pending?: boolean }>> =>
    apiClient.post('/users', data),

  /**
   * Record explicit consent decisions made at registration time.
   * Sent immediately after a successful register() call so the audit trail
   * reflects the user's affirmative act (GDPR Art. 7).
   */
  recordRegistrationConsent: (
    consents: Array<{ consentType: string; action: 'grant' | 'revoke' }>,
  ): Promise<ApiResponse<unknown>> =>
    apiClient.post('/users/gdpr/consent/bulk-update', { consents }),

  /**
   * Logout the current user
   */
  logout: (): Promise<ApiResponse<void>> =>
    apiClient.post('/users/auth/logout'),

  /**
   * Get the current user's profile
   */
  getProfile: (): Promise<User> =>
    apiClient.get('/users/auth/profile').then(response => response.data),

  // ==================== Password Management ====================

  /**
   * Request password reset email
   */
  forgotPassword: (email: string): Promise<ApiResponse<void>> =>
    apiClient.post('/users/auth/forgot-password', { email }),

  /**
   * Reset password with token
   */
  resetPassword: (token: string, password: string): Promise<ApiResponse<void>> =>
    apiClient.post('/users/auth/reset-password', { token, password }),

  /**
   * Change password for current user
   */
  changePassword: (data: { current_password: string; new_password: string }): Promise<ApiResponse<{ access_token: string; expires_in: number }>> =>
    apiClient.post('/users/me/change-password', data),

  /**
   * Validate password strength
   */
  validatePassword: (password: string): Promise<ApiResponse<PasswordValidationResult>> =>
    apiClient.post('/users/validate-password', { password }),

  /**
   * Check if current user must change password
   */
  checkPasswordChangeRequired: (): Promise<ApiResponse<{ force_password_change: boolean; is_oauth_user: boolean }>> =>
    apiClient.get('/users/me/password-change-required'),

  /**
   * Set password for OAuth users (who don't have a password yet)
   */
  setPassword: (data: { password: string; confirm_password?: string }): Promise<ApiResponse<{ message: string }>> =>
    apiClient.post('/users/me/set-password', data),

  /**
   * Get current user's authentication methods
   */
  getAuthMethods: (): Promise<ApiResponse<{ has_password: boolean; has_oauth: boolean; can_set_password: boolean }>> =>
    apiClient.get('/users/me/auth-methods'),

  /**
   * Force password change for a user (admin only). The permission-gated
   * admin endpoint requires a reason for the audit log + user notification;
   * clearing the flag goes through the profile PATCH (form field).
   */
  forcePasswordChange: (userId: string): Promise<ApiResponse<void>> =>
    apiClient.post(`/users/admin/users/${userId}/force-password-change`, { reason: 'Forced by admin' }),

  // ==================== Email Verification ====================

  /**
   * Verify email with token
   */
  verifyEmail: (token: string): Promise<ApiResponse<void>> =>
    apiClient.get(`/users/auth/verify-email/${token}`),

  /**
   * Send a numeric email-verification code (#528 onboarding Step 1).
   * Always 200 on a well-formed request; 429 on resend cooldown / hourly cap.
   */
  sendVerificationCode: (data: { email: string; firstName?: string }): Promise<ApiResponse<{ ttlSec: number; cooldownSec: number }>> =>
    apiClient.post('/users/auth/verify-code/send', data),

  /**
   * Confirm a numeric email-verification code (#528 onboarding Step 1).
   */
  confirmVerificationCode: (data: { email: string; code: string }): Promise<ApiResponse<{ verified: boolean; reason?: 'invalid' | 'expired' | 'locked' | 'no_code'; attemptsLeft?: number }>> =>
    apiClient.post('/users/auth/verify-code/confirm', data),

  // ==================== OAuth ====================

  /**
   * Get Google OAuth redirect URL
   */
  googleAuth: (): string => {
    return '/api/v1/users/auth/google'
  },

  // ==================== MFA ====================

  /**
   * Generate MFA secret and QR code
   */
  generateMFASecret: (): Promise<ApiResponse<{ secret: string; qrCode: string }>> =>
    apiClient.post('/users/mfa/generate-secret'),

  /**
   * Enable MFA for current user
   */
  enableMFA: (code: string): Promise<ApiResponse<{ enabled: boolean }>> =>
    apiClient.post('/users/mfa/enable', { code }),

  /**
   * Disable MFA for current user
   */
  disableMFA: (code: string): Promise<ApiResponse<{ disabled: boolean }>> =>
    apiClient.post('/users/mfa/disable', { code }),

  // ==================== MFA Backup Codes ====================

  /**
   * Get remaining backup codes count
   */
  getBackupCodesCount: (): Promise<ApiResponse<{ remainingCount: number }>> =>
    apiClient.get('/users/mfa/backup-codes'),

  /**
   * Generate new backup codes
   */
  generateBackupCodes: (): Promise<ApiResponse<BackupCodesResponse>> =>
    apiClient.post('/users/mfa/backup-codes/generate'),

  /**
   * Clear all backup codes
   */
  clearBackupCodes: (): Promise<ApiResponse<void>> =>
    apiClient.delete('/users/mfa/backup-codes'),

  // ==================== Session Management ====================

  /**
   * Get current user's active sessions
   */
  getUserSessions: (): Promise<ApiResponse<UserSession[]>> =>
    apiClient.get('/users/me/sessions'),

  /**
   * Revoke a specific session
   */
  revokeSession: (sessionId: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/users/me/sessions/${sessionId}`),

  // ==================== Profile Management ====================

  /**
   * Update current user's profile
   */
  updateProfile: (data: ProfileUpdateRequest): Promise<ApiResponse<User>> =>
    apiClient.put('/users/me/profile', data),

  /**
   * Update the current user's own record — PATCH /users/me is the writer for
   * column fields outside the fixed /users/me/profile write-set
   * (language, timezone, bio).
   */
  updateMe: (data: { language?: string; timezone?: string; bio?: string }): Promise<ApiResponse<User>> =>
    apiClient.patch('/users/me', data),

  /**
   * Delete profile picture
   */
  deleteProfilePicture: (): Promise<ApiResponse<void>> =>
    apiClient.delete('/users/me/profile/picture'),

  /**
   * Get public profile for a user
   */
  getPublicProfile: (userId: string): Promise<ApiResponse<PublicProfile>> =>
    apiClient.get(`/users/${userId}/public`),

  // ==================== Activity Logs ====================

  /**
   * Get current user's activity logs
   */
  getUserActivityLogs: (params?: {
    page?: number
    limit?: number
    action?: string
    resourceType?: string
    resourceId?: string
    startDate?: string
    endDate?: string
    success?: boolean
    ipAddress?: string
    sessionId?: string
  }): Promise<ApiResponse<any[]>> =>
    apiClient.get('/users/activity/me/activity', { params }),

  // ==================== User Management (Backward Compatibility) ====================
  // These methods are also available in usersApi for better domain separation

  /**
   * Get paginated list of users with filters (admin only)
   * @deprecated Use usersApi.getUsers instead
   */
  getUsers: (params?: {
    page?: number
    limit?: number
    search?: string
    userType?: string
    status?: string
    role?: string
    emailVerified?: boolean
    mfaEnabled?: boolean
    dateFrom?: string
    dateTo?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    // #846 — opt in to the hotels/affiliates each account is linked to. Off by
    // default so other consumers of this endpoint (e.g. the affiliate user
    // typeahead) do not pay for lookups they never read.
    includeAssociatedEntities?: boolean
  }): Promise<ApiResponse<User[]>> =>
    apiClient.get('/users', { params }),

  /**
   * Get a specific user by ID (admin only)
   * @deprecated Use usersApi.getUserById instead
   */
  getUserById: (userId: string): Promise<ApiResponse<User>> =>
    apiClient.get(`/users/${userId}`),

  /**
   * Update a user's information (admin only)
   * @deprecated Use usersApi.updateUser instead
   */
  updateUser: (userId: string, data: Partial<User>): Promise<ApiResponse<User>> =>
    apiClient.patch(`/users/${userId}`, data),

  /**
   * Delete a user account (admin only)
   * @deprecated Use usersApi.deleteUser instead
   */
  deleteUser: (userId: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/users/${userId}`),

  // ==================== User Role Management (Backward Compatibility) ====================

  /**
   * Get roles assigned to a user
   * @deprecated Use usersApi.getUserRoles instead
   */
  getUserRoles: (userId: string): Promise<ApiResponse<string[]>> =>
    apiClient.get(`/users/${userId}/roles`),

  /**
   * Assign a role to a user
   * @deprecated Use usersApi.assignRole instead
   */
  assignRole: (userId: string, role: string): Promise<ApiResponse<void>> =>
    apiClient.post(`/users/${userId}/roles`, { role }),

  /**
   * Remove a role from a user
   * @deprecated Use usersApi.removeRole instead
   */
  removeRole: (userId: string, role: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/users/${userId}/roles/${role}`),

  // ==================== Role Management (Backward Compatibility) ====================

  /**
   * Get all available roles (admin only)
   * @deprecated Use usersApi.getAllRoles instead
   */
  getAllRoles: (): Promise<ApiResponse<any[]>> =>
    apiClient.get('/users/roles'),

  /**
   * Create a new role (admin only)
   * @deprecated Use usersApi.createRole instead
   */
  createRole: (roleData: {
    name: string
    description: string
    permissions?: any
  }): Promise<ApiResponse<any>> =>
    apiClient.post('/users/roles', roleData),

  /**
   * Delete a role (admin only)
   * @deprecated Use usersApi.deleteRole instead
   */
  deleteRole: (roleName: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/users/roles/${roleName}`),

  // ==================== Permission Management (Backward Compatibility) ====================

  // ==================== Image Management (Backward Compatibility) ====================

  /**
   * Upload a profile image
   * @deprecated Use usersApi.uploadProfileImage instead
   */
  uploadProfileImage: (file: File): Promise<ApiResponse<{ id: string; filename: string }>> => {
    const formData = new FormData()
    formData.append('image', file)
    // #594: allow large uploads to exceed the global 10s axios timeout.
    return apiClient.post('/users/images/profile', formData, { timeout: 120000 })
  },

  /**
   * Upload a general image
   * @deprecated Use usersApi.uploadImage instead
   */
  uploadImage: (file: File): Promise<ApiResponse<{ id: string; filename: string }>> => {
    const formData = new FormData()
    formData.append('image', file)
    // #594: allow large uploads to exceed the global 10s axios timeout.
    return apiClient.post('/users/images', formData, { timeout: 120000 })
  },

}

export default authApi

// ===========================================================================
// #528 R5 — deferred credential activation (#529).
//
// After an applicant is approved + accepts terms their account becomes
// `active` but PASSWORDLESS. They receive an email linking to
// `/auth/complete-account?token=<raw>` where they set their sign-in
// credentials. These two calls drive that page. Standalone named exports
// (mirrors the R1 `submitHotelSignup` precedent) so they can be re-exported
// from BOTH service barrels.
//   - validateAccountSetup → GET, ALWAYS 200: { valid, firstName?, email? }.
//     An invalid/expired token resolves with `valid: false` (not a 4xx), so
//     the page can render the friendly invalid state without the client
//     interceptor firing a redirect/toast.
//   - completeAccount → POST. 201 sets the auth session cookies (the user is
//     now logged in); 409 `already_completed`; 400 `invalid_or_expired_token`.
// Resolves to /api/v1/users/auth/complete-account[/validate] via the client
// baseURL.
// ===========================================================================

// ===========================================================================
// #528 G1 — Google-at-signup (email-proof + prefill convergence).
//
// "Continue with Google" inside the onboarding identity block calls this to
// BEGIN the Google flow. The backend mints a short-lived server-side
// oauth_state (purpose='onboarding_signup') bound to the chosen type +
// returnPath, and returns the Google consent URL. The FE then performs a full
// page redirect to `authUrl`. After Google, the backend callback redirects
// back to `${returnPath}?google=verified&email&fn&ln` (email proven + Google
// identity parked server-side for auto-link at submit).
//
// 404 `google_signup_disabled` is the backend kill-switch being off — callers
// should treat it as "Google not available" and hide/disable the button.
//
// Standalone named export (mirrors the validateAccountSetup/completeAccount
// precedent above) so it can be re-exported from BOTH service barrels.
// ===========================================================================

interface GoogleOnboardingStartResult {
  authUrl: string
  state: string
  nonce: string
}

export const startGoogleOnboarding = (
  params: { type: 'hotel' | 'affiliate'; returnPath?: string },
): Promise<ApiResponse<GoogleOnboardingStartResult>> =>
  apiClient.post('/users/auth/google/onboarding/start', {
    type: params.type,
    ...(params.returnPath ? { returnPath: params.returnPath } : {}),
  })

interface AccountSetupValidation {
  valid: boolean
  firstName?: string
  email?: string
}

interface CompleteAccountResult {
  user?: User
}

/**
 * Validate an account-setup token from the activation email link.
 * GET — always resolves 200; check `valid` in the body.
 */
export const validateAccountSetup = (
  token: string,
): Promise<ApiResponse<AccountSetupValidation>> =>
  apiClient.get('/users/auth/complete-account/validate', { params: { token } })

/**
 * Set the user's sign-in credentials and consume the activation token.
 * On 201 the backend sets the httpOnly session cookies — the caller should
 * refresh the auth context and navigate into the app.
 */
export const completeAccount = (
  data: { token: string; method: 'password'; password: string },
): Promise<ApiResponse<CompleteAccountResult>> =>
  apiClient.post('/users/auth/complete-account', data)
